import { createAsyncThunk } from '@reduxjs/toolkit';

import { IQuiz } from '.';
import APIDataProvider from '../../dataProvider/APIDataProvider';
import { UserApiPaths } from '../../dataProvider/APIPaths';

export interface IGetAllQuiz {
  limit: number;
  offset: number;
  subject?: string;
  curriculum?: string;
  grade?: string;
  topicId?: string;
  paper?: string;
  subjectLevel?: string;
  subjectCode?: string;
}

export interface IGetMyQuizHistory {
  userId: string;
  limit: number;
  offset: number;
  includeSubmissionCount?: number;
  curriculum?: string;
  subject?: string;
  topicId?: string;
}

const QuizApiDataProvider = new APIDataProvider<IQuiz>();
const QuizBySubTopicIdApiDataProvider = new APIDataProvider<IQuiz>();

const getQuizListModule = async (data: IGetAllQuiz) => {
  let pathUrl = `${UserApiPaths.quizzes}?limit=${data.limit || 30}&offset=${
    data.offset || 0
  }`;

  if (data.subject) {
    pathUrl += `&subject=${data.subject}`;
  }

  if (data.curriculum) {
    pathUrl += `&curriculum=${data.curriculum}`;
  }

  if (data.grade) {
    pathUrl += `&grade=${data.grade}`;
  }

  if (data.topicId) {
    pathUrl += `&topicId=${data.topicId}`;
  }

  if (data.paper) {
    pathUrl += `&paper=${data.paper}`;
  }

  if (data.subjectLevel) {
    pathUrl += `&subjectLevel=${data.subjectLevel}`;
  }

  if (data.subjectCode) {
    pathUrl += `&subjectCode=${data.subjectCode}`;
  }

  const response = await QuizApiDataProvider.get(pathUrl);

  return response.data as {
    offset: number;
    limit: number;
    totalDocs: number;
    docs: Array<IQuiz>;
  };
};

const getQuizHistoryModule = async (data: IGetMyQuizHistory) => {
  let _path = `${UserApiPaths.quizzes}/history?userId=${data.userId}&limit=${data.limit}&offset=${data.offset}`;

  if (data.includeSubmissionCount) {
    _path += `&includeSubmissionCount=${data.includeSubmissionCount}`;
  }

  if (data.curriculum) {
    _path += `&curriculum=${data.curriculum}`;
  }

  if (data.subject) {
    _path += `&subject=${data.subject}`;

    if (data.topicId) {
      _path += `&topicId=${data.topicId}`;
    }
  }

  const response = await QuizApiDataProvider.get(_path);

  return response.data as {
    offset: number;
    limit: number;
    totalDocs: number;
    docs: Array<IQuiz>;
  };
};

export const getQuizList = createAsyncThunk(
  'user/quiz/list',
  getQuizListModule
);

export const loadMoreQuizList = createAsyncThunk(
  'user/quiz/loadMore',
  getQuizListModule
);

export const getQuizById = createAsyncThunk(
  'user/quiz/id',
  async (id: string) => {
    const response = await QuizApiDataProvider.getById(
      `${UserApiPaths.quizzes}/${id}`
    );
    return response.data;
  }
);

export const getQuizBySubTopicId = createAsyncThunk(
  'user/quiz/getBySubTopicId',
  async (data: {
    subTopicId: string;
    paper: string;
    redirectToquizHistory?: boolean;
  }) => {
    const response = await QuizBySubTopicIdApiDataProvider.create(
      `${UserApiPaths.quizzes}/getBySubTopicId`,
      { subTopicId: data.subTopicId, paper: data.paper }
    );

    return response.data;
  }
);

export const getQuizHistory = createAsyncThunk(
  'user/quiz/my-history',
  getQuizHistoryModule
);

export const loadMoreQuizHistory = createAsyncThunk(
  'user/quiz/my-history/loadMore',
  getQuizHistoryModule
);

export const checkAllPapersBySubTopicId = createAsyncThunk(
  'user/quiz/checkPapersBySubTopicId',
  async (subTopicId: string) => {
    const response = await QuizBySubTopicIdApiDataProvider.getById(
      `${UserApiPaths.quizzes}/checkPapersBySubTopicId/${subTopicId}`
    );
    return response.data;
  }
);

export default getQuizList;
